/**
 * Элементы карусели публикации («Что публикуем» на `/posts/:id`): порядок, обложка и рамка элемента.
 * Чистая логика (`node --test`), сервер — `reels/postplan.py` (`_media`, `set_media_crop`); правила
 * повторяют его, чтобы полоса не расходилась с сервером.
 *
 * Лимита элементов сети здесь больше нет (№285): он перестал быть гейтом утверждения и стал
 * поведением сети, а строку предупреждения («возьмёт только обложку, в публикации 2») считает
 * сервер и отдаёт готовой — `PostNetwork.media_note`. Второй расчёт на фронте однажды разошёлся бы
 * с первым, поэтому его тут нет.
 * Импорт — с расширением (модуль грузит `node --test` напрямую, без алиасов и бандлера).
 */
import type { Crop, PostMedia } from "./postPlan.ts";

export type { PostMedia };

/** Обложка — элемент с `sort = 0` (инвариант сервера: это же `post_plan.variant_id`); список пуст —
 * `undefined`. */
export function cover(media: PostMedia[]): PostMedia | undefined {
  return media.find((m) => m.sort === 0) ?? media[0];
}

/**
 * Новый порядок id после сдвига элемента `id` на `dir` (-1 — раньше, +1 — позже); готов к телу
 * `PATCH /api/posts/{id}/media` (`order`). На краю или с неизвестным `id` — порядок не меняется.
 */
export function move(media: PostMedia[], id: number, dir: -1 | 1): number[] {
  const ids = media.map((m) => m.id);
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const out = [...ids];
  const a = out[i] as number;
  const b = out[j] as number;
  out[i] = b;
  out[j] = a;
  return out;
}

/** Склонение «элемент», как `postplan._elements_word`: 1 — «элемент», 2–4 (кроме 11–14) —
 * «элемента», иначе «элементов» (10, 11–20, 25 и т. п.). */
export function elementsWord(n: number): string {
  const mod100 = Math.abs(n) % 100;
  if (mod100 >= 11 && mod100 <= 14) return "элементов";
  const tail = mod100 % 10;
  if (tail === 1) return "элемент";
  if (tail >= 2 && tail <= 4) return "элемента";
  return "элементов";
}

/** Рамка элемента для сети `code`; элемента нет, карты нет или сети в карте нет — `null`. */
export function mediaCrop(m: PostMedia | undefined, code: string): Crop | null {
  return m?.crop?.[code] ?? null;
}

/**
 * Подпись длительности видео-элемента («0:12»): как `trends.durationText`, но определена заново —
 * тот модуль не грузится вне бандлера (тянет `@/lib/api`), а тесты этого модуля идут `node --test`.
 */
export function videoCaption(ms: number | null | undefined): string {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
